import { ReactElement, RefObject, useEffect, useRef, useState } from 'react';
import L, { Map, LatLng, LatLngExpression, Point } from 'leaflet';
import { Circle, useMap } from 'react-leaflet';

import {
	LeafletDragHandleStyler,
	MapTextShape,
	PolylineDecorator,
	RectangleShapeOverwrite
} from '..';
import {
	IMapDrawing,
	ICircle,
	IRectangle,
	IArrowMapDrawing,
	ITextMapDrawing,
	IShape
} from '../../../redux';
import { MapPolylineShape, ShapePopupPanel } from '../../molecules';
import produce from 'immer';
import { RectangleOverwriteLayer } from '../../../utils';

import './map-shape.styles.scss';
import { useCallbackSafeState } from '../../../hooks';
import { useTranslation } from 'react-i18next';

export interface IMapShapeProps {
	drawing: IMapDrawing;
}

export type ShapeLayer =
	| L.Circle
	| L.Rectangle
	| L.Polyline
	| RectangleOverwriteLayer;

export function MapShape(props: IMapShapeProps): ReactElement {
	const { drawing } = props;

	return (
		<>
			<LeafletDragHandleStyler
				drawing={drawing}
				isSelected={drawing.isPopupOpen}
			/>
			<ShapeElement drawing={drawing} />
		</>
	);
}

function ShapeElement(props: { drawing: IMapDrawing }): ReactElement {
	const { drawing } = props;
	const { t } = useTranslation('map');

	switch (drawing.shape.type) {
		case 'circle':
			return <CircleElement drawing={drawing} />;
		case 'rectangle':
			return <RectangleElement drawing={drawing} />;
		case 'polyline':
			return <MapPolylineShape drawing={drawing} />;
		case 'arrow':
			return <PolylineDecorator arrow={drawing as IArrowMapDrawing} />;
		case 'text':
			return <MapTextShape drawing={drawing as ITextMapDrawing} />;
		default:
			throw new Error(
				t('actionButtons.edit.errorCreateShape', {
					shapeType: drawing.shape.type
				})
			);
	}
}

function CircleElement(props: { drawing: IMapDrawing }): ReactElement {
	const { drawing } = props;
	const [layer, setLayer] = useState<L.Circle>();

	const ref: RefObject<L.Circle | undefined> = useRef<L.Circle>();
	const map: Map = useMap();

	const [getShape, setShape] = useCallbackSafeState<ICircle>(
		drawing.shape as ICircle
	);

	useEffect(() => {
		if (ref.current) {
			setLayer(ref.current);
		}
	}, [ref]);

	useEffect(() => {
		setShape(drawing.shape as ICircle);
	}, [drawing]);

	function duplicateShape(offsetFunction: (coord: LatLng) => LatLng): IShape {
		return produce(getShape(), (draft: IShape) => {
			(draft as ICircle).center = offsetFunction(
				(draft as ICircle).center
			);
		});
	}

	function calculatePopupLocation(): LatLngExpression {
		const center: Point = map.latLngToLayerPoint(layer!.getLatLng());
		const radius: number = (layer as any)._radius;

		const point: Point = new Point(center.x, center.y - radius);
		const coord: LatLng = map.layerPointToLatLng(point);

		return [coord.lat, coord.lng];
	}

	return (
		<Circle
			ref={ref as RefObject<L.Circle>}
			fillOpacity={0}
			center={getShape().center}
			radius={getShape().radius}
			color={getShape().color}
			weight={getShape().lineWidth}
		>
			{layer && (
				<ShapePopupPanel
					shapeLayer={layer}
					drawing={drawing}
					createDuplicate={duplicateShape}
					calculatePopupLocation={calculatePopupLocation}
				/>
			)}
		</Circle>
	);
}

function RectangleElement(props: { drawing: IMapDrawing }): ReactElement {
	const { drawing } = props;
	const [getShape, setShape] = useCallbackSafeState<IRectangle>(
		drawing.shape as IRectangle
	);
	const ref: RefObject<RectangleOverwriteLayer | undefined> =
		useRef<RectangleOverwriteLayer>();
	const [layer, setLayer] = useState<RectangleOverwriteLayer>();
	const map: Map = useMap();

	useEffect(() => {
		setShape(drawing.shape as IRectangle);
	}, [drawing.shape]);

	useEffect(() => {
		if (ref.current) {
			setLayer(ref.current);
		}
	}, [ref]);

	function duplicateShape(offsetFunction: (coord: LatLng) => LatLng): IShape {
		return produce(getShape(), (draft: IShape) => {
			(draft as IRectangle).corner1 = offsetFunction(
				(draft as IRectangle).corner1
			);

			(draft as IRectangle).corner2 = offsetFunction(
				(draft as IRectangle).corner2
			);
		});
	}

	function calculatePopupLocation(): LatLngExpression {
		const centerPoint: Point = (
			layer as any
		).editing._getCenterPoint() as Point;
		const corner1: Point = map.latLngToLayerPoint(
			(layer as RectangleOverwriteLayer)._corner1
		);

		const corner2: Point = map.latLngToLayerPoint(
			(layer as RectangleOverwriteLayer)._corner2
		);

		const y: number = Math.min(corner1.y, corner2.y);

		const point: Point = new Point(centerPoint.x, y);
		const coord: LatLng = map.layerPointToLatLng(point);
		return [coord.lat, coord.lng];
	}

	return (
		<RectangleShapeOverwrite
			ref={ref as RefObject<RectangleOverwriteLayer>}
			corner1={getShape().corner1}
			corner2={getShape().corner2}
			pathOptions={{
				fillOpacity: 0,
				color: getShape().color,
				weight: getShape().lineWidth
			}}
		>
			{layer && (
				<ShapePopupPanel
					shapeLayer={layer}
					drawing={drawing}
					createDuplicate={duplicateShape}
					calculatePopupLocation={calculatePopupLocation}
				/>
			)}
		</RectangleShapeOverwrite>
	);
}
